import { IcEdit, IcPlus as IcPlus24, IcStatusLosange, IcTrash } from '@assets/icons/24x24';
import { IcPlus as IcPlus32 } from '@assets/icons/32x32';
import { ButtonGroup, For, HStack, Icon, IconButton, Text, VStack } from '@chakra-ui/react';
import { CockpitPanelIntlKeysEnum } from '@components/cockpit/cockpit.types';
import { CockpitListLayout } from '@components/cockpit/layouts/CockpitListLayout';
import CockpitRow from '@components/cockpit/layouts/CockpitRow';
import Panel from '@components/cockpit/panels/Panel';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasRole } from '@redux/authent/authent.selectors';
import { selectAllOrganizations } from '@redux/config/config.selectors';
import { getClearanceColor } from '@utils/data/notification.utils';
import { Dispatch, SetStateAction, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { RoleEnum } from '@/types/authent/roles.types';
import { Organization } from '@/types/config/config.types';

import CockpitPanelHeader from '../CockpitPanelHeader';
import DeleteOrganizationModal from './DeleteOrganizationModal';
import OrganizationFormContainer from './OrganizationFormContainer';

interface OrganizationsActionsProps {
  canCreateOrganization: boolean;
  organizationsLength: number;
  onCreate: () => void;
}

function OrganizationsActions({
  canCreateOrganization,
  organizationsLength,
  onCreate,
}: Readonly<OrganizationsActionsProps>) {
  return (
    <>
      <Text color="neutral.500" textTransform="lowercase" userSelect="none">
        <FormattedMessage id="components.organizations.result" values={{ count: organizationsLength }} />
      </Text>
      {canCreateOrganization && (
        <IconButton aria-label="Add" variant="transparent" boxSize="56px" marginLeft="auto" onClick={onCreate}>
          <Icon as={IcPlus24} boxSize="24px" />
        </IconButton>
      )}
    </>
  );
}

function OrganizationsHeader() {
  return (
    <>
      <Text width="220px" color="neutral.500">
        <FormattedMessage id="components.organizations.name" />
      </Text>
      <Text width="200px" color="neutral.500">
        <FormattedMessage id="components.organizations.platforms" />
      </Text>
      <Text width="240px" color="neutral.500">
        <FormattedMessage id="components.organizations.email" />
      </Text>
      <Text width="300px" color="neutral.500">
        <FormattedMessage id="components.organizations.defaultClearance" />
      </Text>
    </>
  );
}

interface OrganizationsProps {
  setEditedOrganizationCode: Dispatch<SetStateAction<string | null | undefined>>;
}

function Organizations({ setEditedOrganizationCode }: Readonly<OrganizationsProps>) {
  const organizations = useAppSelector(selectAllOrganizations);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const canCreateOrganization = useAppSelector((state) => hasRole(state, RoleEnum.CREATE_ORGANIZATION));
  const canEditOrganization = useAppSelector((state) => hasRole(state, RoleEnum.EDIT_ORGANIZATION));
  const canDeleteOrganization = useAppSelector((state) => hasRole(state, RoleEnum.DELETE_ORGANIZATION));
  return (
    <CockpitListLayout
      dialog={
        <DeleteOrganizationModal
          selectedOrganization={selectedOrganization}
          setSelectedOrganization={setSelectedOrganization}
        />
      }
      actions={
        <OrganizationsActions
          canCreateOrganization={canCreateOrganization}
          organizationsLength={organizations.length}
          onCreate={() => setEditedOrganizationCode(undefined)}
        />
      }
      header={organizations.length > 0 && <OrganizationsHeader />}
    >
      <For
        each={organizations}
        fallback={
          canCreateOrganization ? (
            <VStack
              padding={4}
              gap={2}
              marginY="auto"
              className="group"
              border="2px solid"
              cursor="pointer"
              borderColor="neutral.600"
              _hover={{ borderColor: 'skyBlue', backgroundColor: 'neutral.800' }}
              onClick={() => setEditedOrganizationCode(undefined)}
            >
              <Icon as={IcPlus32} boxSize="32px" color="neutral.300" _groupHover={{ color: 'skyBlue' }} />
              <Text>
                <FormattedMessage id="components.organizations.createFirst" />
              </Text>
            </VStack>
          ) : undefined
        }
      >
        {(organization, index) => {
          return (
            <CockpitRow index={index} listLength={organizations.length} key={index}>
              <Text width="220px" fontWeight="medium">
                {organization.name}
              </Text>
              <Text width="200px" fontWeight="medium">
                {organization.platforms.length}
              </Text>
              <Text width="240px" fontWeight="medium">
                {organization.email}
              </Text>
              <HStack gap={1} width="300px">
                <Icon
                  as={IcStatusLosange}
                  boxSize="24px"
                  color={getClearanceColor(organization.organizationParameter.defaultClearance)}
                />
                <Text width="100%">
                  <FormattedMessage id={`clearance.${organization.organizationParameter.defaultClearance}`} />
                </Text>
              </HStack>
              <ButtonGroup marginLeft="auto" attached height="100%">
                {canEditOrganization && (
                  <IconButton
                    variant="transparent"
                    boxSize="56px"
                    aria-label="edit"
                    onClick={() => setEditedOrganizationCode(organization.code)}
                  >
                    <Icon as={IcEdit} boxSize="24px" />
                  </IconButton>
                )}
                {canDeleteOrganization && (
                  <IconButton
                    variant="transparent"
                    boxSize="56px"
                    aria-label="delete"
                    onClick={() => setSelectedOrganization(organization)}
                  >
                    <Icon as={IcTrash} boxSize="24px" />
                  </IconButton>
                )}
              </ButtonGroup>
            </CockpitRow>
          );
        }}
      </For>
    </CockpitListLayout>
  );
}

export function OrganizationManagement() {
  const { formatMessage } = useIntl();
  const [editedOrganizationCode, setEditedOrganizationCode] = useState<string | null | undefined>(null);

  return (
    <Panel
      header={
        <CockpitPanelHeader
          label={formatMessage({ id: `components.sidebar.${CockpitPanelIntlKeysEnum.ORGANIZATION}` })}
        />
      }
    >
      {editedOrganizationCode === null ? (
        <Organizations setEditedOrganizationCode={setEditedOrganizationCode} />
      ) : (
        <OrganizationFormContainer
          editedOrganizationCode={editedOrganizationCode}
          setEditedOrganizationCode={setEditedOrganizationCode}
        />
      )}
    </Panel>
  );
}
